import {GitHubAvatar} from '@github-ui/github-avatar'
import {LockIcon, OrganizationIcon, RepoIcon} from '@primer/octicons-react'

import type {
  OrgRepositoryPickerOrganization,
  OrgRepositoryPickerRepository,
  OrgRepositoryPickerSelection,
} from './org-repository-picker-types'

/** Shared size so the org avatar and repo octicon line up across rows and anchors. */
const DESTINATION_VISUAL_SIZE = 16

export function OrganizationDestinationVisual({
  organization,
  size = DESTINATION_VISUAL_SIZE,
}: {
  organization: OrgRepositoryPickerOrganization
  size?: number
}) {
  // Fall back to the org octicon when no avatar URL is available so the row never shows a broken image.
  if (!organization.avatarUrl) return <OrganizationIcon size={size} />
  return <GitHubAvatar src={organization.avatarUrl} size={size} square alt="" />
}

export function RepositoryDestinationVisual({
  repository,
  size = DESTINATION_VISUAL_SIZE,
}: {
  repository: OrgRepositoryPickerRepository
  size?: number
}) {
  const Icon = repository.isPrivate ? LockIcon : RepoIcon
  return <Icon size={size} />
}

/** Announces a repository's visibility so the lock icon isn't the only cue for assistive tech. */
function repositoryAccessibleName(repository: OrgRepositoryPickerRepository): string {
  return `${repository.nameWithOwner}${repository.isPrivate ? ' (private)' : ''}`
}

/**
 * Names the destination type so screen readers can tell an organization destination apart from a
 * repository, rather than relying on the leading icon alone.
 */
export function destinationAccessibleName(selection: OrgRepositoryPickerSelection): string {
  return selection.kind === 'org'
    ? `Organization: ${selection.organization.name}`
    : `Repository: ${repositoryAccessibleName(selection.repository)}`
}

/** Action label for a control that changes the destination. */
export function changeDestinationLabel(selection: OrgRepositoryPickerSelection): string {
  return selection.kind === 'org'
    ? `Change destination, current organization: ${selection.organization.name}`
    : `Change destination, current repository: ${repositoryAccessibleName(selection.repository)}`
}
